import { Button, Modal, SelectMenu, TextField, Typography, Checkbox } from '@scale/scaleui';
import { useCallback, useState } from 'react';
import { useCreateTlcSquad } from 'react-scale/components/ExpertManagement/hooks/mutations/useCreateTlcSquad';
import {
  podTypeOptionsSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { useToggle } from 'react-use-v13';
import { shallow } from 'zustand/shallow';
import { Tooltip } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface CreateSquadModalProps {
  onClose: () => void;
  open: boolean;
  refetch: () => Promise<void>;
  defaultTeamLeadPodType: string;
}

const MAX_PROJECT_GROUP_NAME_LENGTH = 30;

export function CreateSquadModal({
  onClose,
  open,
  refetch,
  defaultTeamLeadPodType,
}: CreateSquadModalProps) {
  const [tlcId, setTlcId] = useState('');
  const [projectGroupName, setProjectGroupName] = useState('');
  const [podType, setPodType] = useState(defaultTeamLeadPodType);
  const [createDiscourseCommunityChannel, toggleCreateDiscourseCommunityChannel] = useToggle(true);
  const podTypeOptions = useStatContextStore(podTypeOptionsSelector, shallow);

  const { mutateAsync: createSquad, isLoading: upsertLoading } = useCreateTlcSquad();

  const handleSave = useCallback(async () => {
    try {
      await createSquad({
        tlcId,
        teamLeadPodTypeId: podType,
        createDiscourseCommunityChannel,
        communityChannelName: `Squad Reviewers ${projectGroupName.trim()}`,
      });
      await refetch();
      onClose();
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    }
  }, [
    createSquad,
    tlcId,
    podType,
    createDiscourseCommunityChannel,
    projectGroupName,
    refetch,
    onClose,
  ]);

  const closeModal = useCallback(() => {
    setTlcId('');
    setProjectGroupName('');
    onClose();
  }, [onClose]);

  return (
    <Modal title="Create TLC Squad" open={open} onClose={closeModal}>
      <div
        className="flex flex-col justify-between"
        style={{ minHeight: '50vh', minWidth: '50vw' }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="col-span-1">TLC ID</Typography.Subtitle3>
            <TextField value={tlcId} onChange={t => setTlcId(t.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="col-span-1">Pod Type</Typography.Subtitle3>
            <SelectMenu
              selectedOptionId={podType}
              setSelected={r => setPodType(r)}
              options={podTypeOptions}
            />
          </div>
          <Checkbox
            className="modal-child"
            label="Create Discourse community channel for project's squad reviewers"
            checked={createDiscourseCommunityChannel}
            onChange={toggleCreateDiscourseCommunityChannel}
          />
          {createDiscourseCommunityChannel && (
            <div className="flex flex-col gap-1">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row items-center gap-1">
                  <Typography.Subtitle3 className="col-span-1">
                    Project group name
                  </Typography.Subtitle3>
                  <Tooltip
                    title={`Your Discourse community channel and category will be named "Squad Reviewers {Project group name}"`}
                    placement="bottom"
                    arrow
                    interactive
                  >
                    <FontAwesomeIcon icon="info-circle" />
                  </Tooltip>
                </div>
                <Typography.Paragraph3
                  className={
                    'text-xs ' +
                    (projectGroupName.length <= MAX_PROJECT_GROUP_NAME_LENGTH
                      ? 'text-gray-400'
                      : 'text-danger-400')
                  }
                >
                  {projectGroupName.length}/{MAX_PROJECT_GROUP_NAME_LENGTH}
                </Typography.Paragraph3>
              </div>
              <TextField
                value={projectGroupName}
                onChange={t => setProjectGroupName(t.target.value)}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-1">
          <Button onClick={onClose} variant="danger">
            Cancel
          </Button>
          <Button
            disabled={
              upsertLoading ||
              !tlcId ||
              (createDiscourseCommunityChannel &&
                (!projectGroupName.trim() ||
                  projectGroupName.length > MAX_PROJECT_GROUP_NAME_LENGTH))
            }
            onClick={handleSave}
          >
            {upsertLoading ? 'Creating...' : 'Create Squad'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
